import React, { useEffect, useRef, useState } from 'react';
import { getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';
import { PenTool } from 'lucide-react';
import AWS from 'aws-sdk';
import SignatureCanvas from 'react-signature-canvas';
import jsPDF from 'jspdf';
// no html2canvas in final PDF composition
// Load pdfjs only when generating the final PDF to keep UI fast

// Stable child component to avoid remounting signature canvases on parent re-render
function SmoothSignature({ sigRef, height = 100, onEnd }) {
  const wrapRef = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = wrapRef.current;
    const cssW = Math.max(320, Math.floor(el?.clientWidth || 640));
    setWidth(cssW);
    // No resize listener to avoid clearing during signing
  }, []);
  const style = { width: `${width}px`, height: `${height}px`, touchAction: 'none', display: 'block', cursor: 'crosshair' };
  return (
    <div ref={wrapRef} className="w-full">
      {width > 0 && (
        <SignatureCanvas
          ref={sigRef}
          penColor="#111"
          minWidth={0.8}
          maxWidth={2.2}
          throttle={16}
          minDistance={4}
          velocityFilterWeight={0.6}
          clearOnResize={false}
          backgroundColor="rgb(255,255,255)"
          canvasProps={{ width, height, style }}
          onEnd={onEnd}
        />
      )}
    </div>
  );
}

function StoreLogo() {
  return (
    <div className="flex items-center gap-3 justify-center mb-8">
      <img src="/Logo.png" alt="StoreHere" className="h-16 w-auto" />
      <div>
        <span className="text-2xl font-bold text-gray-800">store</span>
        <span className="text-2xl font-bold text-orange-500">here</span>
        <div className="text-sm text-gray-500 uppercase tracking-wider">SELF STORAGE</div>
      </div>
    </div>
  );
}

function Signature() {
  const [userAttributes, setUserAttributes] = useState({});
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  // New inputs
  const insuranceSigRef = useRef(null);
  const creditSigRef = useRef(null);
  const consentSigRef = useRef(null);

  // Capture signatures directly from canvas refs on submit to avoid re-renders clearing the canvas
  const [clientName, setClientName] = useState('');

  const [insuranceChoice, setInsuranceChoice] = useState('');
  const [ackReadFullTerms, setAckReadFullTerms] = useState(false);
  const [ackAgreeTermsPrivacy, setAckAgreeTermsPrivacy] = useState(false);
  const [ackDefaultConsequences, setAckDefaultConsequences] = useState(false);

  // Tenant information from S3
  const [tenantUrl, setTenantUrl] = useState(null);
  const [tenantKey, setTenantKey] = useState(null);
  const [tenantLoading, setTenantLoading] = useState(false);
  // UI perf: remove viewport-coupled PDF rendering
  const tenantAckRef = useRef(null);
  const insuranceSectionRef = useRef(null);
  const creditSectionRef = useRef(null);
  const consentSectionRef = useRef(null);
  const nameInputRef = useRef(null);
  const [tenantInfoAcknowledged, setTenantInfoAcknowledged] = useState(false);
  const insuranceCreditRef = useRef(null);
  const consentRef = useRef(null);
  const pageRef = useRef(null);

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      await getCurrentUser();
      const attributes = await fetchUserAttributes();
      setUserAttributes(attributes);
    } catch (error) {
      console.error('Error loading user:', error);
      window.location.href = '/signin';
    } finally {
      setLoading(false);
    }
  };

  // Configure AWS SDK for S3
  AWS.config.update({
    region: process.env.REACT_APP_AWS_REGION,
    accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
    secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY
  });
  const s3 = new AWS.S3();

  // Do not auto-fetch heavy tenant info; fetch lazily on user click

  const loadTenantInformation = async () => {
    try {
      setTenantLoading(true);
      setTenantUrl(null);
      setTenantKey(null);
      const list = await s3
        .listObjectsV2({ Bucket: 'storehere-agreements', Prefix: 'tenant-information/' })
        .promise();
      const items = list.Contents || [];
      if (items.length > 0) {
        items.sort((a, b) => new Date(b.LastModified) - new Date(a.LastModified));
        const key = items[0].Key;
        const url = await s3.getSignedUrlPromise('getObject', {
          Bucket: 'storehere-agreements',
          Key: key,
          Expires: 3600
        });
        setTenantKey(key);
        setTenantUrl(url);
      }
    } catch (e) {
      console.error('Error loading tenant information:', e);
    } finally {
      setTenantLoading(false);
    }
  };

  // Ensure we have a current signed URL for tenant info when generating the PDF
  const getLatestTenantInfo = async () => {
    if (tenantUrl && tenantKey) return { url: tenantUrl, key: tenantKey };
    try {
      const list = await s3
        .listObjectsV2({ Bucket: 'storehere-agreements', Prefix: 'tenant-information/' })
        .promise();
      const items = list.Contents || [];
      if (items.length > 0) {
        items.sort((a, b) => new Date(b.LastModified) - new Date(a.LastModified));
        const key = items[0].Key;
        const url = await s3.getSignedUrlPromise('getObject', {
          Bucket: 'storehere-agreements',
          Key: key,
          Expires: 3600
        });
        setTenantKey(key);
        setTenantUrl(url);
        return { url, key };
      }
    } catch (e) {
      console.error('Error ensuring tenant info:', e);
    }
    return { url: tenantUrl, key: tenantKey };
  };

  // Removed inline PDF rasterization for performance

  // Signature helpers
  const clearSig = (ref) => {
    if (ref?.current) ref.current.clear();
  };

  const saveSig = (ref, setter) => {
    if (!ref?.current) return;
    if (ref.current.isEmpty()) setter(null);
    else setter(ref.current.toDataURL());
  };

  // Helpers for PDF composition: load pdfjs on-demand to keep UI snappy
  const renderPdfToImages = async (url) => {
    if (!url) return [];
    const [{ default: pdfjsLib }, { default: workerSrc }] = await Promise.all([
      import('pdfjs-dist/build/pdf'),
      import('pdfjs-dist/build/pdf.worker.min.js'),
    ]);
    pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;
    let pdf;
    try {
      const resp = await fetch(url);
      const buf = await resp.arrayBuffer();
      pdf = await pdfjsLib.getDocument({ data: buf }).promise;
    } catch (e) {
      pdf = await pdfjsLib.getDocument(url).promise;
    }
    const images = [];
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const v = page.getViewport({ scale: 1.6 });
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = Math.floor(v.width);
      canvas.height = Math.floor(v.height);
      await page.render({ canvasContext: ctx, viewport: v }).promise;
      images.push(canvas.toDataURL('image/png'));
    }
    return images;
  };

  const loadImageAsDataUrl = (src) => new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

  const addFullPageImage = (doc, dataUrl) => {
    if (!dataUrl) return;
    const imgProps = doc.getImageProperties(dataUrl);
    const pdfW = doc.internal.pageSize.getWidth();
    const pdfH = doc.internal.pageSize.getHeight();
    const ratio = Math.min(pdfW / imgProps.width, pdfH / imgProps.height);
    const w = imgProps.width * ratio;
    const h = imgProps.height * ratio;
    const x = 0;
    const y = 0;
    doc.addImage(dataUrl, 'PNG', x, y, w, h, undefined, 'FAST');
  };

  const addWrappedText = (doc, text, x, y, maxWidth, lineHeight = 6) => {
    const lines = doc.splitTextToSize(text, maxWidth);
    lines.forEach((ln) => {
      doc.text(ln, x, y);
      y += lineHeight;
    });
    return y;
  };

  const drawCheckbox = (doc, x, y, label, checked) => {
    doc.setDrawColor(0);
    doc.rect(x, y - 4, 4, 4);
    if (checked) {
      doc.setFontSize(12);
      doc.text('X', x + 1.2, y + 0.8);
    }
    doc.setFontSize(12);
    doc.text(label, x + 7, y);
  };

  const addLink = (doc, label, x, y, url) => {
    const origColor = doc.getTextColor();
    doc.setTextColor(42, 109, 176);
    try {
      if (typeof doc.textWithLink === 'function') {
        doc.textWithLink(label, x, y, { url });
      } else {
        doc.text(label, x, y);
        const w = doc.getTextWidth(label) || 40;
        // clickable rectangle around the text
        doc.link(x, y - 4, w, 6, { url });
      }
    } catch (e) {
      // fallback to plain text
      doc.text(label, x, y);
    } finally {
      doc.setTextColor(origColor);
    }
  };

  // Format date as DD/MM/YYYY
  const formatDate = (date = new Date()) => {
    const d = new Date(date);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  // SmoothSignature defined at top-level

  const generateAndUploadPDF = async () => {
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const marginX = 12;
      const contentW = pageW - marginX * 2;

      let isFirstPage = true;
      const ensurePage = () => {
        if (isFirstPage) { isFirstPage = false; return; }
        doc.addPage();
      };
      const maybeNewPage = (neededHeight = 40) => {
        if (y + neededHeight > pageH - 16) {
          doc.addPage();
          y = 16;
        }
      };

      // 1) Links page — match UI: provide links to tenant info and contract
      ensurePage();
      doc.setFontSize(18); doc.setTextColor(20);
      doc.text('Storage Agreement', marginX, 20);
      doc.setFontSize(12); doc.setTextColor(0);
      let y = 32;
      doc.setFontSize(14); doc.text('Documents', marginX, y); y += 8;
      const { url: latestTenantUrl } = await getLatestTenantInfo();
      const contractUrl = 'https://storehere-agreements.s3.ap-southeast-2.amazonaws.com/photos/Contract+Agreement+STORE+HERE+EDWARDSTOWN+2025_organized.pdf';
      addLink(doc, 'Tenant Information (open link)', marginX, y, latestTenantUrl || window.location.origin);
      y += 8;
      addLink(doc, 'Contract Agreement (open link)', marginX, y, contractUrl);
      y += 12;
      doc.setFontSize(12);
      drawCheckbox(doc, marginX, y, 'I have read the following and understand the tenant information.', !!tenantInfoAcknowledged);
      y += 10;
      doc.text(`Name: ${clientName || ''}`, marginX, y); y += 8;
      doc.text(`Date: ${formatDate(new Date())}`, marginX, y); y += 10;

      // 3) No inline screenshots to keep output concise

      // 4) Agreement sections — mirror the visible UI layout (packed, minimal whitespace)
      // Continue on same page where possible
      // INSURANCE block
      doc.setFillColor(20, 50, 74); // #14324A
      doc.rect(0, 0, pageW, 12, 'F');
      doc.setTextColor(255); doc.setFontSize(12); doc.text('INSURANCE', marginX, 8);
      doc.setTextColor(0); doc.setFontSize(12);
      y = Math.max(y + 6, 24);
      const p1 = 'I/we consent to the FO contacting me/us about any available Customers Goods Insurance or Customer Protection offering. If such an offer is available, further information about the coverage offered will only be provided on request. I/we understand that before making any decision to purchase any such offering I/we must carefully read the relevant Product Disclosure Statement or seek independent advice to ensure the offering is suitable for me/us.';
      y = addWrappedText(doc, p1, marginX, y, contentW);
      doc.setTextColor(42, 109, 176); doc.setFontSize(12);
      y += 4; y = addWrappedText(doc, 'Select one of the following:', marginX, y, contentW);
      doc.setTextColor(0);
      y += 4; drawCheckbox(doc, marginX, y, 'I/we have adequate insurance for the Goods that we are storing and will provide details of this coverage if required.', insuranceChoice === 'adequate');
      y += 8; doc.setTextColor(42, 109, 176); doc.text('OR', marginX, y); doc.setTextColor(0);
      y += 6; drawCheckbox(doc, marginX, y, "I/we don't have insurance and understand that if the Goods are lost or damaged I/we may have to bear that loss. Further, that if my/our Goods cause any damage or loss then I/we may be liable for such damage or loss.", insuranceChoice === 'no_insurance');
      y += 12; doc.setTextColor(42,109,176); doc.text('Signed on behalf of the Storer(s):', marginX, y); doc.setTextColor(0);
      y += 4; {
        const img = insuranceSigRef.current && !insuranceSigRef.current.isEmpty() ? insuranceSigRef.current.toDataURL() : null;
        if (img) doc.addImage(img, 'PNG', marginX, y, contentW * 0.9, 28);
      }
      y += 32;

      // CREDIT CHECK block
      maybeNewPage(60);
      doc.setFillColor(20, 50, 74); doc.rect(0, y, pageW, 12, 'F');
      y += 8; doc.setTextColor(255); doc.text('CREDIT CHECK AND REPORTING CONSENT', marginX, y);
      doc.setTextColor(0); y += 10;
      const creditText = 'I/we acknowledge that the FO may undertake a search of my/our details against the StorerCheck database or with any other applicable credit reporting or identity verification agency, and release my/our details and personal information to StorerCheck or other agency, or where the FO considers appropriate, to a debt collection agency pursuant to clause 13.';
      y = addWrappedText(doc, creditText, marginX, y, contentW);
      y += 8; doc.setTextColor(42,109,176); doc.text('Signed on behalf of the Storer(s):', marginX, y); doc.setTextColor(0);
      y += 4; {
        const img = creditSigRef.current && !creditSigRef.current.isEmpty() ? creditSigRef.current.toDataURL() : null;
        if (img) doc.addImage(img, 'PNG', marginX, y, contentW * 0.9, 28);
      }

      // CONSENT AND AGREEMENT block
      maybeNewPage(110);
      if (y < 16) y = 16;
      doc.setFillColor(234, 242, 251); // #eaf2fb
      doc.rect(0, 0, pageW, 12, 'F');
      doc.setTextColor(20, 50, 74); doc.setFontSize(12);
      doc.text('• PLEASE READ THE FULL TERMS CAREFULLY AS BY SIGNING BELOW YOU WILL BE BOUND BY THEM', marginX, 8);
      doc.setTextColor(0); doc.setFontSize(12);
      y = Math.max(y + 8, 24);
      doc.setTextColor(42,109,176); doc.text('Acknowledgement and agreement — I/we acknowledge and agree that:', marginX, y); doc.setTextColor(0);
      y += 8; drawCheckbox(doc, marginX, y, 'I/we have been provided with reasonable access to, and have read or had the opportunity to read, the Full Terms of this Agreement;', ackReadFullTerms);
      y += 8; drawCheckbox(doc, marginX, y, 'By signing below, I/we will be deemed to have read, understood and agreed to be bound by the terms and conditions of this Agreement and the Privacy Policy;', ackAgreeTermsPrivacy);
      y += 8; drawCheckbox(doc, marginX, y, 'If I/we fail to comply with my/our obligations under this Agreement (including the obligation to pay the Storage Fees when required) the Goods may be sold and/or disposed under clause 13(a)(6).', ackDefaultConsequences);
      y += 14; doc.setTextColor(42,109,176); doc.text('Signed by the Storer(s):', marginX, y); doc.setTextColor(0);
      y += 8; doc.text(`Name: ${clientName}`, marginX, y); y += 6;
      {
        const img = consentSigRef.current && !consentSigRef.current.isEmpty() ? consentSigRef.current.toDataURL() : null;
        if (img) doc.addImage(img, 'PNG', marginX, y, contentW * 0.9, 36);
      }
      y += 40;
      // Facility owner signature
      const ownerSig = await loadImageAsDataUrl('/Josh Kelly_Signature.jpg');
      doc.setTextColor(42,109,176); doc.text('Accepted by Facility Owner', marginX, y); doc.setTextColor(0);
      y += 6; if (ownerSig) doc.addImage(ownerSig, 'JPEG', marginX, y, 60, 20);
      y += 26; doc.text(`Date: ${formatDate(new Date())}`, marginX, y);

      

      // Finalize
      const pdfBlob = doc.output('blob');
      const fileName = `agreements/${userAttributes.sub || 'user'}_${Date.now()}.pdf`;
      await uploadToS3(pdfBlob, fileName);
      return fileName;
    } catch (error) {
      console.error('PDF generation error:', error);
      throw error;
    }
  };

  const uploadToS3 = async (pdfBlob, fileName) => {
    const base64Data = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(',')[1]);
      reader.readAsDataURL(pdfBlob);
    });

    const response = await fetch(`${process.env.REACT_APP_API_URL}/upload-agreement`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fileData: base64Data,
        fileName,
        userInfo: {
          userId: userAttributes.sub,
          email: userAttributes.email
        }
      })
    });

    if (!response.ok) throw new Error('Upload failed');
    return response.json();
  };

  const handleSubmit = async () => {
    // Validation with scroll to specific cell
    const scrollTo = (ref) => ref?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

    if (!tenantInfoAcknowledged) {
      alert('Please confirm you have read and understood the tenant information.');
      scrollTo(tenantAckRef);
      return;
    }
    if (!insuranceChoice) {
      alert('Please select an insurance option.');
      scrollTo(insuranceSectionRef);
      return;
    }
    if (insuranceSigRef.current?.isEmpty()) {
      alert('Please sign under the Insurance section.');
      scrollTo(insuranceSectionRef);
      return;
    }
    if (creditSigRef.current?.isEmpty()) {
      alert('Please sign under the Credit Check and Reporting Consent.');
      scrollTo(creditSectionRef);
      return;
    }
    if (!ackReadFullTerms || !ackAgreeTermsPrivacy || !ackDefaultConsequences) {
      alert('Please tick all consent and acknowledgement checkboxes.');
      scrollTo(consentRef);
      return;
    }
    if (!clientName) {
      alert('Please enter your name.');
      nameInputRef.current?.focus();
      scrollTo(consentRef);
      return;
    }
    if (consentSigRef.current?.isEmpty()) {
      alert('Please sign in the final signature box.');
      scrollTo(consentRef);
      return;
    }

    setProcessing(true);
    try {
      const fileName = await generateAndUploadPDF();
      console.log('Agreement saved:', fileName);
      window.location.href = '/payment';
    } catch (error) {
      console.error('Error saving agreement:', error);
      alert('Error saving agreement. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 overflow-y-scroll">
      {/* Safe-area / notch spacer */}
      <div className="bg-white" style={{ height: 'calc(env(safe-area-inset-top, 0px) + 1rem)' }} />
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <StoreLogo />
            <div className="text-sm text-gray-600">Welcome, {userAttributes.given_name} {userAttributes.family_name}</div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto py-8 px-4">
        <div ref={pageRef} className="bg-white rounded-lg shadow-lg p-6 md:p-8">
          <h1 className="text-3xl font-bold text-center text-gray-800 mb-8">Storage Agreement</h1>

          {/* Tenant information access (no inline heavy rendering) */}
          <div className="mb-6">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-gray-700">View the Tenant Information document and contract agreement in a new tab.</div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      // Fetch and open without updating component state to avoid layout jank
                      const list = await s3
                        .listObjectsV2({ Bucket: 'storehere-agreements', Prefix: 'tenant-information/' })
                        .promise();
                      const items = list.Contents || [];
                      if (items.length === 0) return;
                      items.sort((a, b) => new Date(b.LastModified) - new Date(a.LastModified));
                      const key = items[0].Key;
                      const url = await s3.getSignedUrlPromise('getObject', {
                        Bucket: 'storehere-agreements',
                        Key: key,
                        Expires: 3600
                      });
                      window.open(url, '_blank', 'noopener');
                    } catch (e) {
                      console.error(e);
                    }
                  }}
                  className="inline-flex items-center gap-2 bg-[#14324A] text-white px-4 py-2 rounded hover:opacity-90 focus-visible:outline-none"
                >
                  <PenTool size={16} /> Tenant Information
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const url = 'https://storehere-agreements.s3.ap-southeast-2.amazonaws.com/photos/Contract+Agreement+STORE+HERE+EDWARDSTOWN+2025_organized.pdf';
                    try { window.open(url, '_blank', 'noopener'); } catch (e) { window.location.href = url; }
                  }}
                  className="inline-flex items-center gap-2 bg-gray-800 text-white px-4 py-2 rounded hover:opacity-90 focus-visible:outline-none"
                >
                  Contract (PDF)
                </button>
              </div>
            </div>
          </div>

          {/* Tenant info acknowledgement */}
          <div ref={tenantAckRef} className="mb-6">
            <label className="flex items-start gap-3 text-sm text-gray-800">
              <input type="checkbox" checked={tenantInfoAcknowledged} onChange={(e) => setTenantInfoAcknowledged(e.target.checked)} className="mt-1" />
              <span>I have read the following and understand the tenant information.</span>
            </label>
          </div>

          {/* No inline contract/tenant previews to avoid jank */}

          {/* INSURANCE + CREDIT CHECK AND REPORTING CONSENT */}
          <div ref={insuranceCreditRef} className="mb-10 grid grid-cols-1 md:grid-cols-2 border rounded">
            {/* INSURANCE */}
            <div ref={insuranceSectionRef} className="border-r md:border-r border-gray-300">
              <div className="bg-[#14324A] text-white font-semibold px-4 py-3 uppercase tracking-wide">INSURANCE</div>
              <div className="px-4 py-4 text-gray-800 text-sm leading-relaxed">
                <p className="mb-3">I/we consent to the FO contacting me/us about any available Customers Goods Insurance or Customer Protection offering. If such an offer is available, further information about the coverage offered will only be provided on request. I/we understand that before making any decision to purchase any such offering I/we must carefully read the relevant Product Disclosure Statement or seek independent advice to ensure the offering is suitable for me/us.</p>
                <p className="text-[#2a6db0] font-semibold mb-2">Select one of the following:</p>
                <div className="space-y-2">
                  <label className="flex items-start gap-2"><input type="radio" name="insurance" value="adequate" checked={insuranceChoice === 'adequate'} onChange={(e) => setInsuranceChoice(e.target.value)} className="mt-1" /><span>I/we have adequate insurance for the Goods that we are storing and will provide details of this coverage if required.</span></label>
                  <div className="text-[#2a6db0] font-semibold">OR</div>
                  <label className="flex items-start gap-2"><input type="radio" name="insurance" value="no_insurance" checked={insuranceChoice === 'no_insurance'} onChange={(e) => setInsuranceChoice(e.target.value)} className="mt-1" /><span>I/we don't have insurance and understand that if the Goods are lost or damaged I/we may have to bear that loss. Further, that if my/our Goods cause any damage or loss then I/we may be liable for such damage or loss.</span></label>
                </div>
                <div className="mt-6 text-[#2a6db0] font-semibold">Signed on behalf of the Storer(s):</div>
                <div className="mt-2 border rounded">
                  <SmoothSignature sigRef={insuranceSigRef} height={100} onEnd={undefined} />
                </div>
                <button type="button" onClick={() => clearSig(insuranceSigRef)} className="mt-2 text-xs text-orange-600">Clear</button>
              </div>
            </div>

            {/* CREDIT CHECK */}
            <div ref={creditSectionRef}>
              <div className="bg-[#14324A] text-white font-semibold px-4 py-3 uppercase tracking-wide">CREDIT CHECK AND REPORTING CONSENT</div>
              <div className="px-4 py-4 text-gray-800 text-sm leading-relaxed">
                <p>I/we acknowledge that the FO may undertake a search of my/our details against the StorerCheck database or with any other applicable credit reporting or identity verification agency, and release my/our details and personal information to StorerCheck or other agency, or where the FO considers appropriate, to a debt collection agency pursuant to clause 13.</p>
                <div className="mt-6 text-[#2a6db0] font-semibold">Signed on behalf of the Storer(s):</div>
                <div className="mt-2 border rounded">
                  <SmoothSignature sigRef={creditSigRef} height={100} onEnd={undefined} />
                </div>
                <button type="button" onClick={() => clearSig(creditSigRef)} className="mt-2 text-xs text-orange-600">Clear</button>
              </div>
            </div>
          </div>

          {/* CONSENT, ACKNOWLEDGEMENT AND AGREEMENT */}
          <div ref={consentRef} className="mb-10 border rounded">
            <div className="bg-[#eaf2fb] text-[#14324A] px-4 py-3 font-semibold"><span className="mr-2">•</span>PLEASE READ THE FULL TERMS CAREFULLY AS BY SIGNING BELOW YOU WILL BE BOUND BY THEM</div>
            <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-300">
              <div className="p-4 md:col-span-3">
                <div className="text-[#2a6db0] font-semibold mb-2">Acknowledgement and agreement</div>
                <div className="text-[#2a6db0] font-semibold mb-1">I/we acknowledge and agree that:</div>
                <div className="space-y-2 text-sm">
                  <label className="flex items-start gap-2"><input type="checkbox" checked={ackReadFullTerms} onChange={(e) => setAckReadFullTerms(e.target.checked)} className="mt-1" /><span>I/we have been provided with reasonable access to, and have read or had the opportunity to read, the Full Terms of this Agreement;</span></label>
                  <label className="flex items-start gap-2"><input type="checkbox" checked={ackAgreeTermsPrivacy} onChange={(e) => setAckAgreeTermsPrivacy(e.target.checked)} className="mt-1" /><span>by signing below, I/we will be deemed to have read, understood and agreed to be bound by the terms and conditions of this Agreement and the Privacy Policy;</span></label>
                  <label className="flex items-start gap-2"><input type="checkbox" checked={ackDefaultConsequences} onChange={(e) => setAckDefaultConsequences(e.target.checked)} className="mt-1" /><span>if I/we fail to comply with my/our obligations under this Agreement (including the obligation to pay the Storage Fees when required) the Goods may be sold and/or disposed under clause 13(a)(6).</span></label>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 border-t">
              <div className="p-4 md:col-span-2">
                <div className="text-[#2a6db0] font-semibold mb-1">Signed by the Storer(s) <span className="text-gray-500 font-normal">NB: If Storer is a company, two signatures may be required (unless the company has a sole director / secretary).</span></div>
                <label className="block text-xs text-gray-600 mb-1">Name</label>
                <input ref={nameInputRef} value={clientName} onChange={(e) => setClientName(e.target.value)} className="w-full border rounded px-3 py-2 mb-3" placeholder="Type your full name" />
                <label className="block text-xs text-gray-600 mb-1">Signature</label>
                <div className="border rounded">
                  <SmoothSignature sigRef={consentSigRef} height={120} onEnd={undefined} />
                </div>
                <button type="button" onClick={() => clearSig(consentSigRef)} className="mt-2 text-xs text-orange-600">Clear</button>
              </div>
              <div className="p-4 border-t md:border-t-0 md:border-l">
                <div className="text-[#2a6db0] font-semibold mb-2">Accepted by Facility Owner</div>
                <div className="text-sm text-gray-700 mb-2">Signed for or on behalf of the FO:</div>
                <div className="border rounded p-2 bg-white flex items-center justify-center" style={{ height: '120px' }}>
                  <img src="/Josh Kelly_Signature.jpg" alt="Facility owner signature" className="max-h-full object-contain" />
                </div>
                <div className="mt-4">
                  <div className="text-[#2a6db0] font-semibold">Date:</div>
                  <div className="mt-1 p-2 border rounded bg-gray-50">{(() => { const d = new Date(); const dd = String(d.getDate()).padStart(2,'0'); const mm = String(d.getMonth()+1).padStart(2,'0'); const yyyy = d.getFullYear(); return `${dd}/${mm}/${yyyy}`; })()}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="text-center">
            <button onClick={handleSubmit} className="bg-orange-500 hover:bg-orange-600 text-white py-3 px-8 rounded-lg font-semibold text-lg disabled:opacity-50" disabled={processing}>
              {processing ? 'Saving Agreement...' : 'Sign Agreement & Continue to Payment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Signature;
